import {
  Group,
  PerspectiveCamera,
  PlaneGeometry,
  Points,
  Scene,
  ShaderMaterial,
  TextureLoader,
  Vector2,
  WebGLRenderer
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { fragmentShader, rotationShader, simplexNoiseShader, vertexShader } from "./shaders.js";

const IMAGE_SOURCES = [
  "/images/hero/selected-icons/0.png",
  "/images/hero/selected-icons/1.png",
  "/images/hero/selected-icons/2.png",
  "/images/hero/selected-icons/3.png"
];
const WHEEL_THRESHOLD_PX = 90;
const WHEEL_RESET_MS = 320;
const WHEEL_COOLDOWN_MS = 700;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const clampScope = (value, min, max) => clamp((value - min) / (max - min), 0, 1);
const frameLerp = (current, target, deltaFrames) => current + (target - current) * (1 - Math.pow(0.9, deltaFrames));

function normalizedWheelDelta(event, viewportHeight) {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 16;
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * viewportHeight;
  return event.deltaY;
}

function showFallback(container, canvas) {
  container.dataset.runicWebgl = "fallback";
  container.classList.add("wf-webgl-fallback");
  if (canvas) canvas.hidden = true;
  if (container.querySelector(".wf-runic-fallback")) return;
  const fallback = document.createElement("div");
  fallback.className = "wf-runic-fallback";
  fallback.innerHTML = '<img src="/images/logo.svg" alt="" width="266" height="39">';
  container.appendChild(fallback);
}

export function initRunicWebGL(container, options = {}) {
  const canvas = container && container.querySelector("canvas");
  const reducedMotion = Boolean(options.reducedMotion);
  if (!container || !canvas || reducedMotion || !("WebGLRenderingContext" in window)) {
    if (container) showFallback(container, canvas);
    return { setHolding() {}, advance() {}, destroy() {} };
  }

  let renderer;
  try {
    renderer = new WebGLRenderer({ antialias: false, alpha: true, canvas, powerPreference: "high-performance" });
  } catch (error) {
    console.warn("Korel particle renderer could not be created.", error);
    showFallback(container, canvas);
    return { setHolding() {}, advance() {}, destroy() {} };
  }

  let disposed = false;
  let animationFrame = null;
  let lastFrameTime = performance.now();
  let isHeroVisible = false;
  let currentProgress = 0;
  let targetProgress = 0;
  let blastProgress = 0;
  let blastTarget = 0;
  let blastLogoIndex = null;
  let holding = false;
  let wheelAccumulator = 0;
  let previousWheelDirection = 0;
  let lastWheelTime = 0;
  let wheelLockedUntil = 0;
  const mouseTarget = { x: 0, y: 0 };
  const mouseCurrent = { x: 0, y: 0 };
  const loadedTextures = [];
  const items = [];

  const scene = new Scene();
  const group = new Group();
  scene.add(group);
  const camera = new PerspectiveCamera(50, 1, 1, 10000);
  camera.position.set(0, 0, 800);
  const renderPass = new RenderPass(scene, camera);
  const bloomPass = new UnrealBloomPass(new Vector2(1, 1), 0.5, 0.12, 0.3);
  const outputPass = new OutputPass();
  const composer = new EffectComposer(renderer);
  composer.addPass(renderPass);
  composer.addPass(bloomPass);
  composer.addPass(outputPass);

  const isInViewport = () => {
    const rect = container.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  };

  const resize = () => {
    if (disposed) return;
    const width = Math.max(container.clientWidth, 1);
    const height = Math.max(container.clientHeight, 1);
    const ratio = window.innerWidth > 1024 ? 1 : window.devicePixelRatio;
    renderer.setPixelRatio(ratio);
    renderer.setSize(width, height, false);
    composer.setPixelRatio(ratio);
    composer.setSize(width, height);
    camera.aspect = width / height;
    camera.position.set(0, 0, 800);
    camera.updateProjectionMatrix();
  };

  const renderItems = (deltaFrames) => {
    currentProgress = frameLerp(currentProgress, targetProgress, deltaFrames);
    blastProgress = frameLerp(blastProgress, blastTarget, deltaFrames * (blastTarget > blastProgress ? 0.55 : 1));
    if (Math.abs(currentProgress - targetProgress) < 0.0001) currentProgress = targetProgress;
    if (Math.abs(blastProgress - blastTarget) < 0.0005) blastProgress = blastTarget;
    items.forEach((item, index) => {
      const inProgress = clampScope(currentProgress, index - 0.9, index);
      const outProgress = clampScope(currentProgress, index, index + 0.3);
      item.material.uniforms.u_time.value += deltaFrames;
      item.material.uniforms.u_inProgress.value = inProgress;
      item.material.uniforms.u_outProgress.value = outProgress;
      item.material.uniforms.u_blastProgress.value = index === blastLogoIndex ? blastProgress : 0;
      item.points.visible = inProgress > 0 && outProgress < 1;
      item.points.rotation.y = (1 - inProgress) * Math.PI * 0.5 + outProgress * Math.PI * -2;
    });
  };

  const animate = (time) => {
    animationFrame = null;
    if (disposed || !isHeroVisible || document.hidden) return;
    const deltaFrames = clamp((time - lastFrameTime) / (1000 / 60), 0.25, 2);
    lastFrameTime = time;
    mouseCurrent.x = frameLerp(mouseCurrent.x, mouseTarget.x, deltaFrames);
    mouseCurrent.y = frameLerp(mouseCurrent.y, mouseTarget.y, deltaFrames);
    group.position.x = window.innerWidth >= 768 ? 180 + mouseCurrent.x * -25 : 0;
    group.position.y = mouseCurrent.y * 25;
    group.rotation.x = mouseCurrent.y * Math.PI * 0.1;
    group.rotation.y = mouseCurrent.x * Math.PI * 0.2;
    renderItems(deltaFrames);
    if (blastLogoIndex !== null && !holding && blastProgress === 0) {
      blastLogoIndex = null;
      container.dataset.blastActive = "false";
    }
    composer.render();
    animationFrame = requestAnimationFrame(animate);
  };

  const start = () => {
    if (disposed || !isHeroVisible || document.hidden || animationFrame !== null) return;
    lastFrameTime = performance.now();
    animationFrame = requestAnimationFrame(animate);
  };
  const stop = () => {
    if (animationFrame === null) return;
    cancelAnimationFrame(animationFrame);
    animationFrame = null;
  };
  const move = (event) => {
    mouseTarget.x = (event.clientX - window.innerWidth / 2) / (window.innerWidth / 2);
    mouseTarget.y = (event.clientY - window.innerHeight / 2) / (window.innerHeight / 2);
  };
  const wheel = (event) => {
    if (!isHeroVisible || !isInViewport()) return;
    const now = performance.now();
    const delta = normalizedWheelDelta(event, container.clientHeight);
    if (!delta) return;
    const direction = Math.sign(delta);
    if (now - lastWheelTime > WHEEL_RESET_MS || (previousWheelDirection && direction !== previousWheelDirection)) wheelAccumulator = 0;
    lastWheelTime = now;
    previousWheelDirection = direction;
    if (now < wheelLockedUntil) { wheelAccumulator = 0; return; }
    wheelAccumulator += delta;
    if (Math.abs(wheelAccumulator) < WHEEL_THRESHOLD_PX) return;
    targetProgress = clamp(targetProgress + Math.sign(wheelAccumulator), 0, IMAGE_SOURCES.length - 1);
    container.dataset.activeLogo = String(targetProgress);
    wheelAccumulator = 0;
    wheelLockedUntil = now + WHEEL_COOLDOWN_MS;
  };
  const visibility = () => document.hidden ? stop() : start();
  const contextLost = (event) => { event.preventDefault(); stop(); showFallback(container, canvas); };

  const intersectionObserver = new IntersectionObserver(([entry]) => {
    isHeroVisible = Boolean(entry && entry.isIntersecting);
    container.dataset.heroVisible = String(isHeroVisible);
    if (isHeroVisible) start(); else { wheelAccumulator = 0; stop(); }
  }, { threshold: 0.01 });
  const resizeObserver = new ResizeObserver(resize);
  window.addEventListener("mousemove", move, { passive: true });
  window.addEventListener("wheel", wheel, { passive: true });
  document.addEventListener("visibilitychange", visibility);
  canvas.addEventListener("webglcontextlost", contextLost);
  intersectionObserver.observe(container);
  resizeObserver.observe(container);
  resize();
  isHeroVisible = isInViewport();
  container.dataset.heroVisible = String(isHeroVisible);
  container.dataset.activeLogo = "0";
  container.dataset.runicWebgl = "active";

  const textureLoader = new TextureLoader();
  Promise.all(IMAGE_SOURCES.map(source => textureLoader.loadAsync(source)))
    .then(textures => {
      if (disposed) { textures.forEach(texture => texture.dispose()); return; }
      textures.forEach(texture => {
        loadedTextures.push(texture);
        const geometry = new PlaneGeometry(550, 550, 150, 150);
        const material = new ShaderMaterial({
          vertexShader: simplexNoiseShader + rotationShader + vertexShader,
          fragmentShader: simplexNoiseShader + rotationShader + fragmentShader,
          uniforms: {
            u_time: { value: 0 }, u_map: { value: texture }, u_inProgress: { value: 1 },
            u_outProgress: { value: 0 }, u_blastProgress: { value: 0 },
            u_rotationRadius: { value: 5 }, u_PointSize: { value: 20 }
          },
          depthWrite: false,
          transparent: true
        });
        const points = new Points(geometry, material);
        points.visible = false;
        group.add(points);
        items.push({ geometry, material, points });
      });
      renderItems(1);
      composer.render();
      start();
    })
    .catch(error => {
      if (disposed) return;
      console.warn("Korel particle textures could not be loaded.", error);
      showFallback(container, canvas);
      stop();
    });

  return {
    setHolding(value) {
      holding = value;
      if (holding) {
        blastLogoIndex = targetProgress;
        blastTarget = 1;
        container.dataset.blastActive = "true";
      } else {
        blastTarget = 0;
      }
      start();
    },
    advance() {
      if (blastLogoIndex === null) blastLogoIndex = targetProgress;
      targetProgress = clamp(targetProgress + 1, 0, IMAGE_SOURCES.length - 1);
      container.dataset.activeLogo = String(targetProgress);
      blastTarget = 0;
      start();
    },
    destroy() {
      disposed = true;
      stop();
      window.removeEventListener("mousemove", move);
      window.removeEventListener("wheel", wheel);
      document.removeEventListener("visibilitychange", visibility);
      canvas.removeEventListener("webglcontextlost", contextLost);
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      items.forEach(({ geometry, material, points }) => { group.remove(points); geometry.dispose(); material.dispose(); });
      loadedTextures.forEach(texture => texture.dispose());
      scene.remove(group);
      scene.clear();
      bloomPass.dispose();
      outputPass.dispose();
      composer.dispose();
      renderer.renderLists.dispose();
      renderer.dispose();
    }
  };
}
